/**
 * public.ts — progressive enhancement for the public site (slice A-3, S1 first).
 * Every screen this bundle touches is real server-rendered markup first; a visitor
 * with JS disabled gets the complete page with every link working (design.md §2.3,
 * §7.3) — nothing here is required, it only adds the loader, the hero video, and the
 * rail's scroll-position gauge on top of a page that already works without it.
 */

import { initHomePage } from "./home/index";
import { bindScrub, initScrubs } from "./home/scrub";
import { initNav } from "./chrome/nav";

declare global {
  interface Window {
    __afawyBoot?: boolean;
    emitEvent?: (name: string, properties?: Record<string, unknown>) => void;
    getAnalyticsDistinctId?: () => string | null;
  }
}

const INTRO_SESSION_KEY = "afawy_intro";
const INTRO_EXIT_AT_MS = 1420;
const INTRO_EXIT_FADE_MS = 260;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// THE FEED safety net (layouts/base.html) drops js-motion unless this is set.
window.__afawyBoot = true;

// The CSS failsafe (onrecord.css §24) reveals JS-hidden content at 3s if this
// bundle never runs; this stamp is how it knows the bundle IS alive.
document.documentElement.classList.add("bundle-live");

/**
 * The ON RECORD first-visit intro (docs/design-on-record/motion-spec.md §2:
 * "the viewport is the frame"). The nonced head snippet decides eligibility
 * BEFORE first paint (home route + not seen this session + motion allowed) and
 * stamps `intro-pending` on <html>, whose CSS ::after covers the viewport so
 * nothing flashes; this function then builds the real overlay: four HUD corners
 * clip in at the viewport corners, the calligraphy WRITES ITSELF right→left
 * (body pieces wipe, dots stamp — keyed to the inline SVG's path IDs), the
 * AFAWY letters type on (EN pages only), and the overlay hands off to the hero
 * choreography (`intro-played` on <html>). Skippable by pointer/key/wheel; the
 * timeline is an authored ceiling, never a wait-for-assets gate.
 */
function initIntro(): void {
  const root = document.documentElement;
  if (!root.classList.contains("intro-pending")) return;
  try {
    sessionStorage.setItem(INTRO_SESSION_KEY, "1");
  } catch {
    /* storage may be unavailable — the intro still plays this once */
  }

  const markSvg = document.querySelector(".site-nav .wordmark svg");
  const overlay = document.createElement("div");
  overlay.className = "intro";
  overlay.setAttribute("aria-hidden", "true");

  for (const pos of ["tl", "tr", "bl", "br"]) {
    const corner = document.createElement("span");
    corner.className = `hud-corner hud-corner--${pos}`;
    overlay.appendChild(corner);
  }

  const center = document.createElement("div");
  center.className = "intro__mark";
  if (markSvg) center.appendChild(markSvg.cloneNode(true));
  overlay.appendChild(center);

  if (root.lang === "en") {
    const latin = document.createElement("div");
    latin.className = "intro__latin";
    latin.setAttribute("aria-hidden", "true");
    "AFAWY".split("").forEach((letter, i) => {
      const span = document.createElement("span");
      span.textContent = letter;
      span.style.setProperty("--i", String(i));
      latin.appendChild(span);
    });
    overlay.appendChild(latin);
  }

  document.body.appendChild(overlay);
  // The overlay now owns the cover — drop the CSS ::after pre-paint cover.
  root.classList.remove("intro-pending");
  root.classList.add("intro-running");

  let exited = false;
  const exit = (): void => {
    if (exited) return;
    exited = true;
    overlay.classList.add("is-exit");
    root.classList.add("intro-played");
    window.setTimeout(() => overlay.remove(), INTRO_EXIT_FADE_MS);
    window.removeEventListener("pointerdown", exit);
    window.removeEventListener("keydown", exit);
    window.removeEventListener("wheel", exit);
    window.removeEventListener("touchmove", exit);
  };
  window.addEventListener("pointerdown", exit, { passive: true });
  window.addEventListener("keydown", exit);
  window.addEventListener("wheel", exit, { passive: true });
  window.addEventListener("touchmove", exit, { passive: true });

  // Double-rAF so the initial (pre-.is-run) styles commit before animating.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => overlay.classList.add("is-run")),
  );
  window.setTimeout(exit, INTRO_EXIT_AT_MS);
}

/**
 * THE CLOCK (motion-spec §1) — the only number permitted to move anywhere on the
 * site: live Asia/Kuwait HH:MM:SS + DD.MM.YYYY in the hero HUD, 1s hard swap.
 * Server-rendered text is the no-JS floor; this replaces it once per second.
 */
function initClock(): void {
  const timeEl = document.querySelector<HTMLElement>("[data-clock]");
  if (!timeEl) return;
  const dateEl = document.querySelector<HTMLElement>("[data-clock-date]");
  const timeFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kuwait",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const dateFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kuwait",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const tick = (): void => {
    const now = new Date();
    timeEl.textContent = timeFmt.format(now);
    if (dateEl) dateEl.textContent = dateFmt.format(now).replace(/\//g, ".");
  };
  tick();
  window.setInterval(tick, 1000);
}

/**
 * STATUS SWAP (motion-spec §1) — the HUD readout cycles the four real mechanism
 * statements as hard textContent swaps every 2800ms. No easing, no fade: a
 * camera readout, not a carousel. Reduced motion: the first message stays.
 */
function initStatusSwap(): void {
  const el = document.querySelector<HTMLElement>("[data-status-swap]");
  if (!el || prefersReducedMotion()) return;
  const raw = el.getAttribute("data-status-items");
  if (!raw) return;
  let items: string[];
  try {
    items = JSON.parse(raw) as string[];
  } catch {
    return;
  }
  if (!Array.isArray(items) || items.length < 2) return;
  let i = 0;
  window.setInterval(() => {
    i = (i + 1) % items.length;
    el.textContent = items[i] ?? "";
  }, 2800);
}

/**
 * The reveal driver (motion-spec §4) — one rAF-throttled scroll listener drives
 * every `[data-reveal]` entrance (SHUNT/STAMP/WIPE/FLOOD live in onrecord.css)
 * AND the pinned scroll scene's `--p`. Latched: `.is-on` never comes back off,
 * so reverse scrolling never re-hides content (acceptance #26). Sibling stagger
 * indexes (`--i`) are assigned here so templates never carry inline styles (CSP).
 */
function initScrollDriver(): void {
  const reveals = Array.from(
    document.querySelectorAll<HTMLElement>("[data-reveal]"),
  );
  // Assign --i per parent group for the CSS stagger math.
  const groups = new Map<HTMLElement | null, number>();
  for (const el of reveals) {
    const parent = el.parentElement;
    const next = groups.get(parent) ?? 0;
    el.style.setProperty("--i", String(Math.min(next, 5))); // cap 6 in flight
    groups.set(parent, next + 1);
  }

  const reduced = prefersReducedMotion();
  if (reduced) {
    for (const el of reveals) el.classList.add("is-on");
  }

  const sceneStage = document.querySelector<HTMLElement>(".scene-stage");
  const scene = sceneStage?.closest<HTMLElement>(".scene") ?? null;
  const heroMedia = document.querySelector<HTMLElement>(
    ".has-hero .hero-media",
  );
  let pending = reveals.filter((el) => !el.classList.contains("is-on"));
  let ticking = false;

  const frame = (): void => {
    ticking = false;
    const vh = window.innerHeight;
    if (pending.length > 0) {
      const still: HTMLElement[] = [];
      for (const el of pending) {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.86 && rect.bottom > 0) {
          el.classList.add("is-on");
        } else {
          still.push(el);
        }
      }
      pending = still;
    }
    if (scene && sceneStage && !reduced) {
      const rect = scene.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) {
        const total = rect.height - vh;
        const p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
        sceneStage.style.setProperty("--p", p.toFixed(4));
      }
    }
    if (heroMedia && !reduced) {
      const y = window.scrollY;
      if (y < vh * 1.2) {
        heroMedia.style.transform = `translate3d(0, ${Math.min(y * 0.1, vh * 0.06).toFixed(1)}px, 0) scale(1.06)`;
      }
    }
  };
  const onScroll = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  onScroll();
}

/**
 * The hero background video (docs/03c-placeholder-assets.md §7.3): `data-src`, never
 * `src`, so nothing is requested until this function decides to swap it in. Not
 * requested at all below 768px, under `navigator.connection.saveData`, or under
 * reduced motion — the poster is the hero in all three cases, asserted as ZERO
 * network requests, not merely a hidden element.
 */
function initHeroVideo(): void {
  const video = document.getElementById("heroVideo");
  if (!(video instanceof HTMLVideoElement)) return;
  const media = video.closest<HTMLElement>(".hero-media");

  // design.md §8.5 fallback ladder step 2: when the band can show NEITHER a
  // video NOR its poster, it must degrade to a DELIBERATE flat `--color-surface`
  // band carrying only the stencil seam — never broken `<video>` chrome, and
  // never the wash multiplying that flat surface into mud (the wash exists to
  // grade real footage, not a solid colour). `.hero-media--degraded` (public.css)
  // hides the video + wash + scrim so the flat surface reads clean. We can't
  // observe the poster's own load on the element, so on a media error we probe
  // the poster URL directly; only if IT also fails do we drop to the flat band —
  // a working poster with a failed video is ladder step 1 and needs no change.
  const degrade = (): void => media?.classList.add("hero-media--degraded");
  const probePosterThenDegrade = (): void => {
    const posterUrl = video.getAttribute("poster");
    if (!posterUrl) {
      degrade();
      return;
    }
    const probe = new Image();
    probe.addEventListener("error", degrade);
    probe.src = posterUrl;
  };
  video.addEventListener("error", probePosterThenDegrade);

  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  if (connection?.saveData) return;
  if (prefersReducedMotion()) return;
  if (!window.matchMedia("(min-width: 768px)").matches) return;

  const sources = video.querySelectorAll<HTMLSourceElement>("source[data-src]");
  for (const source of sources) {
    const dataSrc = source.getAttribute("data-src");
    if (dataSrc) source.src = dataSrc;
  }
  video.load();
  // `error` on the element does not fire reliably when every `<source>` is
  // exhausted, so also check the network state once loading settles: NO_SOURCE
  // means nothing was playable — run the same poster probe / flat-band ladder.
  if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
    probePosterThenDegrade();
  }
  video.play().catch(() => {
    // Autoplay refused (design.md §8.5 fallback ladder step 1) — the poster stays
    // and the layout is already complete; no error state, no play button.
  });
}

/**
 * The has-hero nav (design.md §8.4, docs/03-design.md S1 "Nav"). On S1/S3 the
 * nav floats TRANSPARENT over the hero media, kept legible by the hero scrim.
 * But that scrim lives inside `.hero-media` and scrolls away with it, so a nav
 * left permanently transparent (`.has-hero .site-nav`) ends up over the opaque
 * deck with its light wordmark/links colliding with the deck's own light copy
 * for the rest of the scroll — the design review flagged this on home and
 * for-creators. So transparency is CONDITIONAL: the `site-nav--over-hero` class
 * is present only while the hero media band still reaches below the nav's bottom
 * edge; the instant that band scrolls above the nav, the class drops and the
 * base opaque `.site-nav` covers whatever passes under it. CSS defaults to
 * opaque, so a no-JS visitor gets the safe, legible band and never a collision.
 */
function initHeroNav(): void {
  const nav = document.querySelector<HTMLElement>(".site-nav");
  const media = document.querySelector<HTMLElement>(".hero-media");
  if (!nav || !media || !document.body.classList.contains("has-hero")) return;

  const navHeight = (): number => nav.getBoundingClientRect().height;
  const setOverHero = (over: boolean): void => {
    nav.classList.toggle("site-nav--over-hero", over);
  };

  // Synchronous initial state — set before the observer's first async callback
  // so a fresh load at the top of the page never flashes the opaque band over
  // the hero: transparent while the media's bottom edge is still below the nav.
  setOverHero(media.getBoundingClientRect().bottom > navHeight());

  if (!("IntersectionObserver" in window)) return;

  // Shrinking the root's top edge down by the nav height makes `.hero-media`
  // "intersect" this root exactly while part of it is still below the nav's
  // bottom — i.e. while it is the surface the nav floats on. Once its bottom
  // scrolls above the nav it stops intersecting and the nav goes opaque.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) setOverHero(entry.isIntersecting);
    },
    { rootMargin: `-${Math.round(navHeight())}px 0px 0px 0px`, threshold: 0 },
  );
  observer.observe(media);
}

/**
 * The fixed rail (design.md §11.3): a page-progress gauge, public site only,
 * >=1024px. Hidden over the FIRST deck, live from the second deck onward on every
 * screen — on S1 that first deck is the hero, per design.md's "hidden over the
 * hero, live from DECK 02 onward"; S2 has no hero, so this generalises the same
 * rule to "hidden over the opening deck" rather than hardcoding the string
 * "hero", which would (and initially did) only ever match S1. Purely decorative
 * (`rail-pointer-events: none`) — a visitor with JS disabled simply never sees
 * it, since `.rail` has no server-rendered visible state.
 */
function initRail(): void {
  const rail = document.querySelector<HTMLElement>("[data-rail]");
  const decks = document.querySelectorAll<HTMLElement>("[data-rail-deck]");
  if (!rail || decks.length === 0 || !("IntersectionObserver" in window))
    return;

  const firstDeckKey = decks[0]?.getAttribute("data-rail-deck") ?? null;

  const notches = new Map<string, HTMLElement>();
  rail.querySelectorAll<HTMLElement>("[data-rail-notch]").forEach((notch) => {
    const key = notch.getAttribute("data-rail-notch");
    if (key) notches.set(key, notch);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const key = entry.target.getAttribute("data-rail-deck");
        if (!key) continue;
        const notch = notches.get(key);
        if (notch)
          notch.setAttribute("data-active", String(entry.isIntersecting));
        if (key === firstDeckKey) {
          rail.setAttribute("data-rail-visible", String(!entry.isIntersecting));
        }
      }
    },
    { rootMargin: "-50% 0px -50% 0px" },
  );
  decks.forEach((deck) => observer.observe(deck));
}

/**
 * `whatsapp_cta_clicked` (docs/02c-analytics-spec.md §3) — the one client event
 * with a real call site today. `surface` comes from the element's own data
 * attribute rather than being hardcoded per page, so this one listener covers
 * every screen's WhatsApp chip without a per-screen wiring function.
 */
function initWhatsappTracking(): void {
  document
    .querySelectorAll<HTMLElement>("[data-analytics-whatsapp-surface]")
    .forEach((el) => {
      el.addEventListener("click", () => {
        const surface = el.getAttribute("data-analytics-whatsapp-surface");
        window.emitEvent?.("whatsapp_cta_clicked", { surface });
      });
    });
}

const SLOW_THRESHOLD_MS = 8000;

/**
 * `[data-busy-form]` — ported from `office.ts` (same design.md §11.12 contract,
 * a separate esbuild bundle so it can't just be imported). Extended to also
 * read-only `<textarea>` fields, which S4's "what do you need?" field needs and
 * no office form has had until now. Never calls `preventDefault()` — the form
 * still submits natively, so the whole flow degrades to a plain working POST
 * with no JS (design.md S4 "Loading" state).
 */
function initBusyForms(): void {
  document
    .querySelectorAll<HTMLFormElement>("[data-busy-form]")
    .forEach((form) => {
      form.addEventListener("submit", () => {
        const submit = form.querySelector<HTMLButtonElement>(
          'button[type="submit"]',
        );
        if (!submit || submit.dataset.busyApplied === "true") return;
        submit.dataset.busyApplied = "true";

        const busyLabel = submit.getAttribute("data-busy-label");
        const slowLabel = submit.getAttribute("data-slow-label");
        const tick = document.createElement("span");
        tick.className = "busy-tick";
        tick.setAttribute("aria-hidden", "true");
        for (let i = 0; i < 12; i += 1) {
          const mark = document.createElement("span");
          mark.className = "busy-tick__mark";
          tick.appendChild(mark);
        }

        const labelSpan = document.createElement("span");
        labelSpan.textContent = busyLabel ?? submit.textContent ?? "";

        submit.textContent = "";
        submit.appendChild(tick);
        submit.appendChild(labelSpan);
        // `readOnly`, never `disabled` — see office.ts's identical comment: a
        // `disabled` field is dropped from the form data the browser is about to
        // serialize for THIS submission, since disabling synchronously inside the
        // `submit` handler runs before the default action reads the form.
        submit.disabled = true;
        form.setAttribute("aria-busy", "true");
        form
          .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
            "input, textarea",
          )
          .forEach((field) => {
            if (
              !(field instanceof HTMLInputElement) ||
              field.type !== "hidden"
            ) {
              field.readOnly = true;
            }
          });

        if (slowLabel) {
          setTimeout(() => {
            labelSpan.textContent = slowLabel;
          }, SLOW_THRESHOLD_MS);
        }
      });
    });
}

/* ── Shared field validation for the two public forms (design.md S4:1095 /
   S6:1258) ──────────────────────────────────────────────────────────────────
   Both forms carry `novalidate`, so the browser's own (non-localised) validation
   bubble never preempts — the previous behaviour, which handed an Arabic-locale
   visitor an English browser message and left the bilingual `.field__error`
   markup (server-only) unreachable. `required`/`type` stay in the markup for AT
   semantics and for `checkValidity()` (which still reports validity under
   `novalidate`); the server re-validates and localises on a plain POST as the
   no-JS floor. Each validated `.field` carries `data-error-message` with the
   server-localised string this renders beside it. */
function controlOf(field: HTMLElement): FormControl | null {
  return controlsOf(field)[0] ?? null;
}

type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** All the constraint-bearing controls in a `.field` — usually one, but the DOB
 *  field holds three required selects, so the field is valid only when all of
 *  them are (a single-control check would pass the field on day-only entry). */
function controlsOf(field: HTMLElement): FormControl[] {
  return Array.from(
    field.querySelectorAll<FormControl>(
      "input:not([type=hidden]), select, textarea",
    ),
  );
}

function clearFieldError(field: HTMLElement): void {
  field.classList.remove("field--error");
  field.querySelector("[data-validation-error]")?.remove();
  controlsOf(field).forEach((c) => c.removeAttribute("aria-invalid"));
}

function showFieldError(field: HTMLElement): void {
  field.classList.add("field--error");
  controlsOf(field).forEach((c) =>
    c.setAttribute("aria-invalid", c.checkValidity() ? "false" : "true"),
  );
  // Keep a server-rendered message if one is already there; otherwise render the
  // field's own localised `data-error-message` so the text stays in-language.
  if (field.querySelector(".field__error")) return;
  const message = field.getAttribute("data-error-message");
  if (!message) return;
  const p = document.createElement("p");
  p.className = "field__error";
  p.setAttribute("role", "alert");
  p.setAttribute("data-validation-error", "");
  p.textContent = message;
  field.appendChild(p);
}

/** Validate one `.field` against ALL its controls' constraints, toggling the UI. */
function validateField(field: HTMLElement): boolean {
  const controls = controlsOf(field);
  if (controls.length === 0) return true;
  if (controls.every((c) => c.checkValidity())) {
    clearFieldError(field);
    return true;
  }
  showFieldError(field);
  return false;
}

/** Validate every `[data-error-message]` field under `root`; return the first
 *  invalid one (or null) so the caller can move focus to it. */
function validateFields(root: ParentNode): HTMLElement | null {
  let firstInvalid: HTMLElement | null = null;
  root
    .querySelectorAll<HTMLElement>("[data-error-message]")
    .forEach((field) => {
      if (!validateField(field) && !firstInvalid) firstInvalid = field;
    });
  return firstInvalid;
}

/** On blur (once focus actually leaves the field — so tabbing day→month in the
 *  DOB group does not fire a premature "incomplete" error), validate the field;
 *  once it has errored, re-check on input/change so the message clears the
 *  instant the visitor fixes it (never nags before the first blur). */
function wireBlurValidation(root: ParentNode): void {
  root
    .querySelectorAll<HTMLElement>("[data-error-message]")
    .forEach((field) => {
      controlsOf(field).forEach((control) => {
        control.addEventListener("blur", (event) => {
          const next = (event as FocusEvent).relatedTarget;
          if (next instanceof Node && field.contains(next)) return;
          validateField(field);
        });
        const recheck = (): void => {
          if (field.classList.contains("field--error")) validateField(field);
        };
        control.addEventListener("input", recheck);
        control.addEventListener("change", recheck);
      });
    });
}

function focusField(field: HTMLElement | null): void {
  const control = field ? controlOf(field) : null;
  if (!control) return;
  control.focus();
  control.scrollIntoView({ block: "center" });
}

/** design.md S4:1107 / S6:1274 Offline state: on a submit while the browser
 *  reports offline, block the native POST (which would navigate to the browser's
 *  own error page) and reveal the in-page offline plate — every entered value is
 *  kept. Registered AFTER the validation guard, so an invalid form shows its
 *  field errors first; `stopImmediatePropagation` stops the analytics/busy submit
 *  listeners after it, so a blocked send neither fires analytics nor locks the
 *  form. `navigator.onLine === false` is the reliable direction (a true value
 *  does not guarantee reachability, but false reliably means offline). */
function wireOfflineGuard(form: HTMLFormElement): void {
  const plate = form
    .closest(".enquiry-form, .apply-form")
    ?.querySelector<HTMLElement>("[data-offline-plate]");
  if (!plate) return;
  form.addEventListener("submit", (event) => {
    if (navigator.onLine !== false) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    plate.hidden = false;
    plate.focus();
    plate.scrollIntoView({ block: "center" });
  });
}

/**
 * S4's enquiry form (`docs/03-design.md` S4): `business_enquiry_started` fires on
 * the FIRST field's focus, once — never autofocused server-side, since an
 * autofocus would fire this on every pageview and destroy the funnel's meaning.
 * Also populates the hidden `ph_distinct_id` field (ADR 014 §"identity
 * continuity") and preserves entered values in `sessionStorage` across a
 * language switch (a full navigation, per the lang-toggle's own `<a href>`) —
 * never `localStorage`: values must not survive past this browser session
 * (design.md S4 "First-time vs returning").
 */
function initEnquiryForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-enquiry-form]");
  if (!form) return;

  const STORAGE_KEY = "ugc_enquiry_draft";
  const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "input:not([type=hidden]), textarea",
  );

  // Field validation (design.md S4:1095). Registered FIRST so a blocked submit's
  // `stopImmediatePropagation` also stops the draft-clear/analytics submit
  // listeners below — a failed submit must leave the draft intact.
  wireBlurValidation(form);
  form.addEventListener("submit", (event) => {
    const firstInvalid = validateFields(form);
    if (firstInvalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      focusField(firstInvalid);
    }
  });
  wireOfflineGuard(form);
  // A server round-trip that bounced the POST re-renders with `.field--error`
  // baked in; take keyboard/AT users to the first failure (design.md S4:1095).
  const serverError = form.querySelector<HTMLElement>(".field--error");
  if (serverError) focusField(serverError);

  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const values = JSON.parse(saved) as Record<string, string>;
      fields.forEach((field) => {
        if (field.name in values && !field.value)
          field.value = values[field.name]!;
      });
    }
  } catch {
    // Corrupt or inaccessible sessionStorage — proceed with an empty form
    // rather than block the page on a draft-restore failure.
  }

  const persistDraft = () => {
    const values: Record<string, string> = {};
    fields.forEach((field) => {
      values[field.name] = field.value;
    });
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch {
      // Storage full/disabled — the form still works, it just won't survive a
      // language switch this time.
    }
  };
  fields.forEach((field) => field.addEventListener("input", persistDraft));

  form.addEventListener("submit", () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clean up if storage was never writable.
    }
  });

  let started = false;
  fields.forEach((field) => {
    field.addEventListener(
      "focus",
      () => {
        if (started) return;
        started = true;
        window.emitEvent?.("business_enquiry_started", {});
      },
      { once: true },
    );
  });

  // Read at SUBMIT time, not page load: `analytics.ts` initialises posthog-js via
  // a dynamic `import()`, which resolves after this script's own top-level code
  // has already run — populating the field here at load time would race it and
  // usually lose. By submit time a human has spent at least a few seconds on the
  // form, which is ample.
  form.addEventListener("submit", () => {
    const distinctIdField = form.querySelector<HTMLInputElement>(
      '[name="ph_distinct_id"]',
    );
    if (distinctIdField) {
      distinctIdField.value = window.getAnalyticsDistinctId?.() ?? "";
    }
  });
}

/**
 * S6's stepped creator application (`docs/03-design.md` S6). `apply.html` ships
 * as ONE continuous form — all three fieldsets visible, one submit button — so
 * a no-JS visitor gets the complete working form including the safety-critical
 * server-side 18+ gate (see that template's own header). This function is what
 * turns it into the stepped experience: hides steps 2–3, injects a step
 * indicator and Next/Back controls, runs the client-side 18+ pre-check on
 * step 1's forward action (design.md: "evaluated client-side on the step-1
 * forward action, never on keystroke"), and wires the in-place privacy
 * disclosure toggle (F2). Every string it renders comes off `[data-apply-form]`'s
 * own data attributes — translated server-side — so no copy is hardcoded here.
 */
function initApplyForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-apply-form]");
  if (!form) return;

  const steps = Array.from(
    form.querySelectorAll<HTMLFieldSetElement>("[data-step]"),
  );
  if (steps.length === 0) return;

  const stepPrefix = form.getAttribute("data-step-prefix") ?? "";
  const stepSeparator = form.getAttribute("data-step-separator") ?? "";
  const nextLabel = form.getAttribute("data-next-label") ?? "";
  const backLabel = form.getAttribute("data-back-label") ?? "";
  const underAgeLabel = form.getAttribute("data-under-age-label") ?? "";
  const underAgeNote = form.getAttribute("data-under-age-note") ?? "";

  const indicator = document.createElement("p");
  indicator.className = "step-indicator";
  form.insertBefore(indicator, steps[0] ?? null);

  const controls = document.createElement("div");
  controls.className = "step-controls";
  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "chip chip--ghost";
  backBtn.textContent = backLabel;
  const nextBtn = document.createElement("button");
  nextBtn.type = "button";
  nextBtn.className = "chip";
  nextBtn.textContent = nextLabel;
  controls.appendChild(backBtn);
  controls.appendChild(nextBtn);

  let current = 0;

  function renderIndicator(): void {
    indicator.textContent = "";
    indicator.appendChild(document.createTextNode(`${stepPrefix} `));
    const currentBdi = document.createElement("bdi");
    currentBdi.dir = "ltr";
    currentBdi.textContent = String(current + 1);
    indicator.appendChild(currentBdi);
    indicator.appendChild(document.createTextNode(` ${stepSeparator} `));
    const totalBdi = document.createElement("bdi");
    totalBdi.dir = "ltr";
    totalBdi.textContent = String(steps.length);
    indicator.appendChild(totalBdi);
  }

  // The submit button already lives inside the last step's own markup
  // (`.apply-submit-row`) — controls are inserted just before it there, and
  // simply appended at the end of every earlier step.
  function placeControls(): void {
    const step = steps[current];
    if (!step) return;
    const submitRow = step.querySelector(".apply-submit-row");
    if (submitRow) {
      step.insertBefore(controls, submitRow);
    } else {
      step.appendChild(controls);
    }
    backBtn.hidden = current === 0;
    nextBtn.hidden = current === steps.length - 1;
  }

  function showStep(index: number): void {
    steps.forEach((step, i) => {
      step.hidden = i !== index;
    });
    current = index;
    renderIndicator();
    placeControls();
  }

  showStep(0);

  let underAgePlate = form.parentElement?.querySelector<HTMLElement>(
    "[data-under-age-plate]",
  );

  function clearUnderAgePlate(): void {
    underAgePlate?.remove();
    underAgePlate = undefined;
  }

  function showUnderAgePlate(): void {
    if (underAgePlate) return;
    const plate = document.createElement("div");
    plate.className = "plate plate--warn";
    plate.setAttribute("role", "alert");
    plate.setAttribute("data-under-age-plate", "");
    const label = document.createElement("p");
    label.className = "plate__label";
    label.textContent = underAgeLabel;
    const body = document.createElement("p");
    body.className = "plate__body";
    body.textContent = underAgeNote;
    plate.appendChild(label);
    plate.appendChild(body);
    form.parentElement?.insertBefore(plate, form);
    underAgePlate = plate;
    plate.scrollIntoView({ block: "center" });
  }

  // Local wall-clock, not UTC (contracts/creator-application.ts's `computeAge`
  // runs server-side in UTC — this is only a pre-check; the server call is the
  // actual enforcement, so a day's tolerance either side of midnight is fine).
  function computeAgeFromStep1(): number | null {
    const day = form.querySelector<HTMLSelectElement>("[data-dob-day]")?.value;
    const month =
      form.querySelector<HTMLSelectElement>("[data-dob-month]")?.value;
    const year =
      form.querySelector<HTMLSelectElement>("[data-dob-year]")?.value;
    if (!day || !month || !year) return null;
    const d = Number(day);
    const m = Number(month);
    const y = Number(year);
    const now = new Date();
    let age = now.getFullYear() - y;
    const hadBirthdayThisYear =
      now.getMonth() + 1 > m ||
      (now.getMonth() + 1 === m && now.getDate() >= d);
    if (!hadBirthdayThisYear) age -= 1;
    return age;
  }

  // Custom bilingual validation on step advance (design.md S6:1258 "As S4"),
  // NOT `reportValidity()` — that draws the browser's native, non-localised
  // bubble, which handed an Arabic-page visitor an English message. `validateFields`
  // is scoped to THIS step's own `[data-error-message]` fields, which also
  // sidesteps the whole-form issue the old code documented (a hidden step-3
  // required control is not excluded from `form`-level validation).
  function validateStep(step: HTMLFieldSetElement): boolean {
    const firstInvalid = validateFields(step);
    if (firstInvalid) {
      focusField(firstInvalid);
      return false;
    }
    return true;
  }

  nextBtn.addEventListener("click", () => {
    const step = steps[current];
    if (!step || !validateStep(step)) return;
    if (current === 0) {
      const age = computeAgeFromStep1();
      if (age !== null && age < 18) {
        showUnderAgePlate();
        return;
      }
      clearUnderAgePlate();
    }
    showStep(current + 1);
  });

  backBtn.addEventListener("click", () => {
    if (current === 0) return;
    showStep(current - 1);
  });

  // F2: the privacy notice opens IN PLACE, never a new tab — a creator who
  // loses the tab loses the form. The `<a href>` stays a real link to the
  // standalone S8 route as the no-JS fallback.
  const privacyToggle = form.querySelector<HTMLAnchorElement>(
    "[data-privacy-toggle]",
  );
  const privacyBack = form.querySelector<HTMLAnchorElement>(
    "[data-privacy-back]",
  );
  const disclosure = document.getElementById("privacy-disclosure");

  privacyToggle?.addEventListener("click", (event) => {
    if (!disclosure) return;
    event.preventDefault();
    disclosure.hidden = false;
    // Expose the disclosure state on the trigger (WAI-ARIA disclosure pattern),
    // not just the focus move — a screen-reader user needs to know it expanded.
    privacyToggle.setAttribute("aria-expanded", "true");
    const heading = disclosure.querySelector<HTMLElement>(".doc__heading");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus();
    }
  });

  privacyBack?.addEventListener("click", (event) => {
    if (!disclosure) return;
    event.preventDefault();
    disclosure.hidden = true;
    privacyToggle?.setAttribute("aria-expanded", "false");
    privacyToggle?.focus();
  });

  // `creator_application_started` on step 1's first-field focus — same
  // contract as `initEnquiryForm`'s `business_enquiry_started`.
  let started = false;
  steps[0]?.querySelectorAll<HTMLElement>("input, select").forEach((field) => {
    field.addEventListener(
      "focus",
      () => {
        if (started) return;
        started = true;
        window.emitEvent?.("creator_application_started", {});
      },
      { once: true },
    );
  });

  // Final-submit validation (design.md S6:1258/1265). The form carries
  // `novalidate`, so this — not the native bubble — is what blocks a bad send
  // and states the reason IN TEXT: the step fields (defensive; the Next button
  // already gates each step, but a keyboard Enter can submit from any step) and
  // the consent box, whose missing-consent reason renders in `[data-consent-error]`.
  // Registered before the analytics/busy submit listeners so a block
  // (`stopImmediatePropagation`) stops them too — a blocked submit must not fire
  // analytics or strand the form in its busy/readonly state.
  const consentBox = form.querySelector<HTMLInputElement>(
    'input[name="consent_processing"]',
  );
  const consentError = form.querySelector<HTMLElement>("[data-consent-error]");
  const consentLabel = consentBox?.closest(".checkbox-field");
  form.addEventListener("submit", (event) => {
    const firstInvalid = validateFields(form);
    const consentMissing = !!consentBox && !consentBox.checked;
    if (consentMissing && consentError) {
      consentError.hidden = false;
      consentLabel?.classList.add("checkbox-field--error");
    }
    if (!firstInvalid && !consentMissing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (firstInvalid) {
      const stepIndex = steps.findIndex((s) => s.contains(firstInvalid));
      if (stepIndex >= 0 && stepIndex !== current) showStep(stepIndex);
      focusField(firstInvalid);
    } else if (consentMissing) {
      consentBox?.focus();
    }
  });
  consentBox?.addEventListener("change", () => {
    if (!consentBox.checked) return;
    if (consentError) consentError.hidden = true;
    consentLabel?.classList.remove("checkbox-field--error");
  });

  // On-blur validation for the step-1 fields (design.md S6:1258 "on blur").
  wireBlurValidation(form);
  wireOfflineGuard(form);
  // A server round-trip that bounced the POST re-renders with `.field--error`
  // baked into whichever step holds it — reveal that step and focus the control.
  const serverError = form.querySelector<HTMLElement>(".field--error");
  if (serverError) {
    const stepIndex = steps.findIndex((s) => s.contains(serverError));
    if (stepIndex >= 0) showStep(stepIndex);
    focusField(serverError);
  }

  // Same submit-time timing rationale as `initEnquiryForm`: `analytics.ts`
  // resolves its posthog-js import asynchronously, so reading the distinct ID
  // at page load would usually race it and lose.
  form.addEventListener("submit", () => {
    const distinctIdField = form.querySelector<HTMLInputElement>(
      '[name="ph_distinct_id"]',
    );
    if (distinctIdField) {
      distinctIdField.value = window.getAnalyticsDistinctId?.() ?? "";
    }
  });
}

/**
 * The Samples page (/:loc/samples).
 *  - Brand bar: the chip of the brand section in view lights up and is kept in
 *    view inside the (horizontally scrolling) bar; chips scroll smoothly.
 *  - Hover preview: on a pointer that can hover, each take plays a muted loop of
 *    its own video over the poster (created on first hover, never preloaded).
 *  - THE REEL PLAYER: tapping a take opens a full-screen <dialog> holding every
 *    take in one vertical, snap-scrolling track. Whichever slide is mostly in view
 *    plays; the rest pause. Sources load only for the visible slide and its
 *    neighbours. A progress line tracks playback; crossing into another brand
 *    flashes a "now showing" card.
 *  - Sound: starts ON (the opening tap is the gesture browsers require). Only a
 *    real autoplay refusal (NotAllowedError) falls back to muted. A play()
 *    interrupted by the next skip rejects with AbortError and is ignored, which
 *    is what used to mute the reel when skipping quickly.
 * Each take is a real link to its video, so without this script a tap still plays.
 */
function initSamples(): void {
  const reduced = prefersReducedMotion();

  // ── brand bar ──
  const bar = document.querySelector<HTMLElement>("[data-brand-bar]");
  const barTrack = bar?.querySelector<HTMLElement>(".brand-bar__track") ?? null;
  // The bar sticks right under the nav, whose height is not fixed (it wraps to
  // two rows on narrow screens), so measure it rather than assume 64px.
  const nav = document.querySelector<HTMLElement>(".site-nav");
  if (bar && nav && typeof ResizeObserver === "function") {
    const pin = (): void => {
      bar.style.top = `${nav.getBoundingClientRect().height}px`;
    };
    new ResizeObserver(pin).observe(nav);
    pin();
  }
  const chips = new Map<string, HTMLElement>();
  for (const chip of document.querySelectorAll<HTMLElement>(
    "[data-brand-chip]",
  )) {
    chips.set(chip.getAttribute("data-brand-chip") ?? "", chip);
    chip.addEventListener("click", (event) => {
      const target = document.getElementById(
        chip.getAttribute("data-brand-chip") ?? "",
      );
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
      history.replaceState(null, "", `#${target.id}`);
    });
  }
  function setActiveChip(key: string): void {
    for (const [k, chip] of chips)
      chip.classList.toggle("is-active", k === key);
    const chip = chips.get(key);
    if (chip && barTrack) {
      const c = chip.getBoundingClientRect();
      const tr = barTrack.getBoundingClientRect();
      if (c.left < tr.left + 16 || c.right > tr.right - 16) {
        barTrack.scrollBy({
          left: c.left - tr.left - 16,
          behavior: reduced ? "auto" : "smooth",
        });
      }
    }
  }
  const sections = Array.from(
    document.querySelectorAll<HTMLElement>("[data-brand-section]"),
  );
  if (sections.length > 0 && chips.size > 0) {
    const visible = new Set<HTMLElement>();
    const spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target as HTMLElement);
          else visible.delete(entry.target as HTMLElement);
        }
        const first = sections.find((s) => visible.has(s));
        if (first) {
          setActiveChip(first.getAttribute("data-brand-section") ?? "");
        } else if (
          sections[0] &&
          sections[0].getBoundingClientRect().top > window.innerHeight * 0.3
        ) {
          // back above the first brand: nothing is "in view", bar back to start
          setActiveChip("");
          barTrack?.scrollTo({
            left: 0,
            behavior: reduced ? "auto" : "smooth",
          });
        }
      },
      // a band across the upper-middle of the screen, below nav + brand bar
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    for (const s of sections) spy.observe(s);
  }

  // ── strip meters (phones) ──
  // Each brand's takes are one horizontal strip on a phone; the yellow head of
  // the brand's rule is that strip's scroll line (home/scrub.ts — shared with
  // the homepage rails): it grows as the strip is swiped forward and shrinks as
  // it is swiped back.
  for (const section of sections) {
    const strip = section.querySelector<HTMLElement>(".sheet");
    const rule = section.querySelector<HTMLElement>(".brand-reel__rule");
    if (strip && rule) bindScrub(strip, rule, "--sheet-progress");
  }

  const takes =
    document.querySelectorAll<HTMLAnchorElement>("[data-reel-open]");
  if (takes.length === 0) return;

  // ── hover preview ──
  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    for (const take of takes) {
      const frame = take.querySelector<HTMLElement>(".take__frame");
      const src = take.getAttribute("data-preview");
      if (!frame || !src) continue;
      let preview: HTMLVideoElement | null = null;
      take.addEventListener("pointerenter", () => {
        if (!preview) {
          preview = document.createElement("video");
          preview.className = "take__preview";
          preview.muted = true;
          preview.loop = true;
          preview.playsInline = true;
          preview.preload = "none";
          preview.setAttribute("aria-hidden", "true");
          preview.tabIndex = -1;
          preview.src = src;
          preview.addEventListener("playing", () =>
            preview?.classList.add("is-playing"),
          );
          frame.insertBefore(preview, frame.children[1] ?? null);
        }
        void preview.play().catch(() => {});
      });
      take.addEventListener("pointerleave", () => {
        if (!preview) return;
        preview.pause();
        preview.classList.remove("is-playing");
      });
    }
  }

  // ── the reel player ──
  const reel = document.querySelector<HTMLDialogElement>("[data-reel]");
  const track = reel?.querySelector<HTMLElement>("[data-reel-track]");
  if (!reel || !track || typeof reel.showModal !== "function") return;
  const slides = Array.from(
    track.querySelectorAll<HTMLElement>("[data-reel-slide]"),
  );
  const videos = slides.map((s) => s.querySelector<HTMLVideoElement>("video"));
  const soundBtn = reel.querySelector<HTMLButtonElement>("[data-reel-sound]");
  const hint = reel.querySelector<HTMLElement>(".reel__hint");
  const progress = reel.querySelector<HTMLElement>("[data-reel-progress]");
  const card = reel.querySelector<HTMLElement>("[data-reel-card]");
  const cardName = reel.querySelector<HTMLElement>("[data-reel-card-name]");
  const cardIndex = reel.querySelector<HTMLElement>("[data-reel-card-index]");
  let muted = false;
  let current = -1;
  let currentBrand = "";
  let opener: HTMLElement | null = null;
  let frame = 0;
  let cardTimer = 0;

  function setSound(nextMuted: boolean): void {
    muted = nextMuted;
    for (const v of videos) if (v) v.muted = muted;
    if (soundBtn) {
      soundBtn.setAttribute("aria-pressed", String(!muted));
      soundBtn.setAttribute(
        "aria-label",
        (muted
          ? soundBtn.getAttribute("data-label-on")
          : soundBtn.getAttribute("data-label-off")) ?? "",
      );
    }
  }

  function load(index: number): void {
    const v = videos[index];
    if (v && !v.getAttribute("src")) {
      const src = v.getAttribute("data-src");
      if (src) v.setAttribute("src", src);
    }
  }

  function tick(): void {
    const v = videos[current];
    if (progress && v && v.duration > 0) {
      progress.style.transform = `scaleX(${Math.min(1, v.currentTime / v.duration)})`;
    }
    frame = window.requestAnimationFrame(tick);
  }

  function showBrandCard(slide: HTMLElement): void {
    if (!card || !cardName || !cardIndex || reduced) return;
    const name = slide.getAttribute("data-brand-name") ?? "";
    if (!name) return;
    cardName.textContent = name;
    cardIndex.textContent = slide.getAttribute("data-brand-index") ?? "";
    card.classList.remove("is-showing");
    void card.offsetWidth; // restart the animation
    card.classList.add("is-showing");
    window.clearTimeout(cardTimer);
    cardTimer = window.setTimeout(
      () => card.classList.remove("is-showing"),
      1500,
    );
  }

  function playCurrent(v: HTMLVideoElement): void {
    v.muted = muted;
    v.play().catch((err: unknown) => {
      // Only a genuine "sound needs a gesture" refusal mutes the reel. An
      // AbortError (this play() was interrupted by the next skip) is expected.
      const name = err instanceof DOMException ? err.name : "";
      if (name !== "NotAllowedError" || muted) return;
      setSound(true);
      if (videos[current] === v) void v.play().catch(() => {});
    });
  }

  function activate(index: number): void {
    if (index === current || index < 0) return;
    slides[current]?.classList.remove("is-current");
    // a take paused earlier is about to play: no flash of its paused mark
    slides[index]?.classList.remove("is-paused");
    slides[index]?.classList.add("is-current");
    current = index;
    videos.forEach((v, i) => {
      if (v && i !== index) v.pause();
    });
    for (const i of [index - 1, index, index + 1]) {
      if (i >= 0 && i < videos.length) load(i);
    }
    const slide = slides[index];
    const brand = slide?.getAttribute("data-brand-key") ?? "";
    if (slide && currentBrand && brand !== currentBrand) showBrandCard(slide);
    currentBrand = brand;
    if (progress) progress.style.transform = "scaleX(0)";
    const v = videos[index];
    if (!v) return;
    v.currentTime = 0;
    playCurrent(v);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activate(slides.indexOf(entry.target as HTMLElement));
        }
      }
    },
    { root: track, threshold: 0.6 },
  );
  for (const slide of slides) observer.observe(slide);

  // Where a smooth step is heading: quick repeated presses build on it rather
  // than on `current`, which only updates once the slide is mostly in view.
  let pending = -1;
  const base = (): number => (pending >= 0 ? pending : current);

  function go(index: number): void {
    const clamped = Math.max(0, Math.min(slides.length - 1, index));
    pending = clamped === current ? -1 : clamped;
    slides[clamped]?.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "start",
    });
  }

  const ENTER = ["is-enter-next", "is-enter-prev"];
  const clearEnter = (slide: HTMLElement): void =>
    slide.classList.remove(...ENTER);

  // A sideways swipe (or ←/→) jumps straight to the neighbouring take and slides
  // it in from the side, instead of scrolling the whole track past it.
  function jump(step: 1 | -1): void {
    const index = base() + step;
    const slide = slides[index];
    if (!slide) return;
    pending = -1;
    track!.scrollTop = slide.offsetTop;
    activate(index);
    if (reduced) return;
    clearEnter(slide);
    void slide.offsetWidth; // restart the animation
    slide.classList.add(step > 0 ? "is-enter-next" : "is-enter-prev");
  }
  for (const slide of slides) {
    // animationcancel too: closing mid-slide-in must not leave it half-applied
    slide.addEventListener("animationend", () => clearEnter(slide));
    slide.addEventListener("animationcancel", () => clearEnter(slide));
  }

  // While the track moves, every brand label hides (CSS .reel.is-moving), so
  // one brand's name never rides over another brand's video.
  let moveTimer = 0;
  track.addEventListener(
    "scroll",
    () => {
      reel.classList.add("is-moving");
      window.clearTimeout(moveTimer);
      moveTimer = window.setTimeout(() => {
        reel.classList.remove("is-moving");
        pending = -1;
      }, 160);
    },
    { passive: true },
  );

  // A sideways trackpad / tilt-wheel gesture is a swipe too: one gesture, one
  // take. A gesture (momentum tail included) only ends after 180ms without a
  // sideways wheel event, so a long flick still moves exactly one take.
  let wheelX = 0;
  let wheelLast = -Infinity;
  let wheelSpent = false;
  track.addEventListener(
    "wheel",
    (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.5) return;
      if (event.timeStamp - wheelLast > 180) {
        wheelX = 0;
        wheelSpent = false;
      }
      wheelLast = event.timeStamp;
      if (wheelSpent) return;
      wheelX += event.deltaX;
      if (Math.abs(wheelX) < 60) return;
      wheelSpent = true;
      jump((rtl ? wheelX < 0 : wheelX > 0) ? 1 : -1);
    },
    { passive: true },
  );

  // Swipe detection: the track is `touch-action: pan-y`, so a mostly
  // horizontal drag (finger or mouse) reaches us instead of the scroller.
  // Forward follows the reading direction: right-to-left on EN, left-to-right
  // on AR, the same way the take strips on the page scroll.
  const rtl = document.documentElement.dir === "rtl";
  let swipe: { id: number; x: number; y: number; t: number } | null = null;
  let swallowClick = false;
  track.addEventListener("pointerdown", (event) => {
    if (!event.isPrimary || event.button > 0) return;
    swipe = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      t: event.timeStamp,
    };
  });
  track.addEventListener("pointercancel", () => {
    swipe = null;
  });
  track.addEventListener("pointerup", (event) => {
    if (!swipe || event.pointerId !== swipe.id) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    const quick = event.timeStamp - swipe.t < 900;
    swipe = null;
    if (Math.hypot(dx, dy) > 10) {
      // any drag — even one too short to swipe — is not a tap: its trailing
      // click (mouse) must not pause the video
      swallowClick = true;
      window.setTimeout(() => (swallowClick = false), 0);
    }
    if (!quick || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) {
      return;
    }
    const forward = rtl ? dx > 0 : dx < 0;
    jump(forward ? 1 : -1);
  });
  track.addEventListener(
    "click",
    (event) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.stopPropagation();
      event.preventDefault();
    },
    true,
  );

  let hintTimer = 0;
  function open(index: number, from: HTMLElement): void {
    opener = from;
    current = -1;
    currentBrand = "";
    pending = -1;
    for (const s of slides) {
      s.classList.remove("is-current");
      clearEnter(s);
    }
    document.documentElement.classList.add("reel-open");
    reel!.showModal();
    hint?.classList.remove("is-gone");
    window.clearTimeout(hintTimer);
    hintTimer = window.setTimeout(() => hint?.classList.add("is-gone"), 2600);
    const slide = slides[index];
    if (slide) track!.scrollTop = slide.offsetTop;
    activate(index);
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(tick);
  }

  for (const take of takes) {
    take.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      open(Number(take.getAttribute("data-reel-open")) || 0, take);
    });
  }

  reel.addEventListener("close", () => {
    for (const v of videos) v?.pause();
    window.cancelAnimationFrame(frame);
    current = -1;
    document.documentElement.classList.remove("reel-open");
    opener?.focus();
  });
  reel
    .querySelector("[data-reel-close]")
    ?.addEventListener("click", () => reel.close());
  reel
    .querySelector("[data-reel-prev]")
    ?.addEventListener("click", () => go(base() - 1));
  reel
    .querySelector("[data-reel-next]")
    ?.addEventListener("click", () => go(base() + 1));
  soundBtn?.addEventListener("click", () => {
    setSound(!muted);
    const v = videos[current];
    if (v && v.paused) void v.play().catch(() => {});
  });
  reel.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "PageDown") {
      event.preventDefault();
      go(base() + 1);
    } else if (event.key === "ArrowUp" || event.key === "PageUp") {
      event.preventDefault();
      go(base() - 1);
    } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const forward = (event.key === "ArrowRight") !== rtl;
      jump(forward ? 1 : -1);
    } else if (event.key === "m" || event.key === "M") {
      soundBtn?.click();
    }
  });
  // tapping the video itself toggles pause, like every feed player
  // (a paused take shows a play mark — CSS .reel__slide.is-paused)
  videos.forEach((v, i) => {
    if (!v) return;
    v.addEventListener("click", () => {
      if (v.paused) void v.play().catch(() => {});
      else v.pause();
    });
    v.addEventListener("play", () => slides[i]?.classList.remove("is-paused"));
    v.addEventListener("pause", () => slides[i]?.classList.add("is-paused"));
  });
}

/**
 * The light/dark toggle (founder decision 2026-10-27; default light). The server
 * already painted the saved theme from the `afawy_theme` cookie (src/server/
 * theme.ts), so this only flips `data-theme` on <html> in place and saves the
 * choice for a year. Where the browser has same-document View Transitions, the
 * new theme opens as an iris from the button itself (the camera-shutter move);
 * the radius/origin are handed to CSS as custom properties on <html>.
 */
function initThemeToggle(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>(
    "[data-theme-toggle]",
  );
  if (buttons.length === 0) return;
  const root = document.documentElement;

  function apply(next: "light" | "dark"): void {
    root.setAttribute("data-theme", next);
    for (const b of buttons)
      b.setAttribute("aria-pressed", String(next === "dark"));
    const scheme = document.querySelector<HTMLMetaElement>(
      'meta[name="color-scheme"]',
    );
    if (scheme) scheme.content = next;
    const tint = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (tint) tint.content = next === "dark" ? "#0b0b0c" : "#ffffff";
    try {
      document.cookie = `afawy_theme=${next}; Path=/; Max-Age=31536000; SameSite=Lax${
        location.protocol === "https:" ? "; Secure" : ""
      }`;
    } catch {
      /* cookies blocked: the switch still works for this page */
    }
  }

  for (const button of buttons) {
    button.addEventListener("click", () => {
      const next =
        root.getAttribute("data-theme") === "dark" ? "light" : "dark";
      const doc = document as Document & {
        startViewTransition?: (cb: () => void) => unknown;
      };
      if (
        prefersReducedMotion() ||
        typeof doc.startViewTransition !== "function"
      ) {
        apply(next);
        return;
      }
      const r = button.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y),
      );
      root.style.setProperty("--iris-x", `${Math.round(x)}px`);
      root.style.setProperty("--iris-y", `${Math.round(y)}px`);
      root.style.setProperty("--iris-r", `${Math.ceil(radius)}px`);
      root.classList.add("theme-switching");
      const vt = doc.startViewTransition(() => apply(next)) as {
        finished?: Promise<unknown>;
      };
      const done = (): void => root.classList.remove("theme-switching");
      if (vt && vt.finished) vt.finished.then(done, done);
      else done();
    });
  }
}

initThemeToggle();
initNav();
initScrubs();
initHomePage();
initIntro();
initClock();
initStatusSwap();
initScrollDriver();
initHeroVideo();
initHeroNav();
initRail();
initWhatsappTracking();
initSamples();
// The form inits register their validation submit listeners FIRST; `initBusyForms`
// must run AFTER them so its readonly-lock/busy-tick handler is registered later
// and a validation-blocked submit (which calls `stopImmediatePropagation`) never
// reaches it. Otherwise the busy handler ran first, marked every field readonly —
// which bars it from constraint validation, so the block never fired — and, on a
// blocked submit, would have stranded the form locked with no way to fix it.
initEnquiryForm();
initApplyForm();
initBusyForms();
